import React, { useContext, useEffect, useRef, useState } from "react";

import { CircularProgress, MenuItem, Select, Table, TableBody, TableHead } from "@mui/material";
import { FiArrowUpCircle, FiMinusCircle } from "react-icons/fi";
import {
  api,
  IKmosV2TimeSlotDateAllocationsResponse,
  KmosAllocation,
} from "../../../logic/api";

import styles from "./KmosView.module.css";
import {
  AllocationCellText,
  AllocationHeaderCellText,
  AllocationCellNumber,
  AllocationHeaderCellNumber,
  AllocationRow,
  AllocationRowClosed,
} from "./StyledTableComponents";
import { SessionContext } from "../../../utility/useSession";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { useTranslation } from "react-i18next";
import { usePreferences } from "../../../utility/usePreferences";

const KmosDialogLanes: React.FunctionComponent<{
  timeSlotAllocations: IKmosV2TimeSlotDateAllocationsResponse[] | null;
  onChange: () => void;
}> = (props) => {
  const { timeSlotAllocations, onChange } = props;
  const deleteAllocationAPI = api.hooks.kmosV2.deleteAllocation();
  const closeAllocationAPI = api.hooks.kmosV2.closeAllocation();
  const openAllocationAPI = api.hooks.kmosV2.openAllocation();
  const prioritiseAllocationAPI = api.hooks.kmosV2.prioritiseAllocation();
  const session = useContext(SessionContext);
  const [saving, setSaving] = useState<boolean>(false);
  const scrollContainerRef = useRef(null);
  const timeSlotAllocationsPreviousLength = useRef(0);
  const { kmosManualAllocationEnabled } = usePreferences();
  const kmosManualAllocationReleased = useReleaseManagement(ReleaseFlag.kmosManualAllocationReleased);

  const closedLanes = timeSlotAllocations?.filter((a) => !a.allocation.active);
  const openLanes = timeSlotAllocations?.filter((a) => a.allocation.active);

  const { t } = useTranslation();

  const getAllocationRowCells = (
    allocationData: IKmosV2TimeSlotDateAllocationsResponse,
    priority?: number
  ) => {
    const isNotMainActiveAllocation =
      allocationData.allocation.active && priority && priority > 1;

    return (
      <>
        <AllocationCellNumber>
          {isNotMainActiveAllocation && (
            <div style={{ cursor: "pointer", lineHeight: 1.1 }} onClick={() => prioritiseAllocation(allocationData.allocation)}>
              <FiArrowUpCircle size={20} />
            </div>
          )}
        </AllocationCellNumber>
        <AllocationCellNumber>
          {priority}
        </AllocationCellNumber>
        <AllocationCellText>
          {allocationData.allocation.location.parentName}
        </AllocationCellText>
        <AllocationCellText>
          {allocationData.allocation.location.name}
        </AllocationCellText>
        <AllocationCellNumber>
          {allocationData.allocation.location.capacity}
        </AllocationCellNumber>
        <AllocationCellNumber>
          {allocationData.metrics.arrived}
        </AllocationCellNumber>
        <AllocationCellNumber>
          {allocationData.metrics.parked}
        </AllocationCellNumber>
        <AllocationCellNumber>
          {allocationData.metrics.spaces}
        </AllocationCellNumber>
        <AllocationCellText>
          <Select
            sx={{
              fontSize: "14px",
              height: "100%",
              '& .MuiSelect-select': {
                padding: '4px 0',
              }
            }}
            value={allocationData.allocation.active ? "1" : "0"}
            variant="standard"
            disableUnderline
            onChange={(event) => {
              if (event.target.value === "0") {
                closeAllocation(allocationData.allocation);
              } else {
                openAllocation(allocationData.allocation);
              }
            }}
          >
            <MenuItem value="1">{t("general.open")}</MenuItem>
            <MenuItem value="0">{t("general.closed")}</MenuItem>
          </Select>
        </AllocationCellText>
        <AllocationCellText>
          <div
            onClick={() => removeAllocation(allocationData.allocation)}
            style={{ width: 16, height: 16 }}
          >
            <FiMinusCircle
              size={16}
              title={t("kmos.labels.removeLane")}
              className={styles.dialogAllocationLaneAction}
            ></FiMinusCircle>
          </div>
        </AllocationCellText>
      </>
    );
  };

  const getAllocationId = (allocation: KmosAllocation) => ({
    timeSlotDate: {
      timeSlot: allocation.timeSlot,
      date: allocation.allocationDate,
    },
    locationID: allocation.location.id,
  });

  const removeAllocation = (allocation: KmosAllocation) => {
    deleteAllocationAPI.send(
      session.current.context,
      { id: getAllocationId(allocation) },
      onChange
    );
  };

  const closeAllocation = (allocation: KmosAllocation) => {
    closeAllocationAPI.send(
      session.current.context,
      getAllocationId(allocation),
      onChange
    );
  };

  const openAllocation = (allocation: KmosAllocation) => {
    openAllocationAPI.send(
      session.current.context,
      getAllocationId(allocation),
      onChange
    );
  };

  const prioritiseAllocation = (allocation: KmosAllocation) => {
    setSaving(true);
    const { id, ordinal } = allocation;
    prioritiseAllocationAPI.send(
      session.current.context,
      {
        allocationId: id,
        ordinal: ordinal - 1,
      },
      () => {
        onChange();
        setSaving(false);
      }
    );
  }

  useEffect(() => {
    const didIncrease = timeSlotAllocationsPreviousLength.current < (timeSlotAllocations?.length || 0);
    timeSlotAllocationsPreviousLength.current = timeSlotAllocations?.length || 0;

    if (!scrollContainerRef.current || !didIncrease) {
      return;
    }

    const scrollContainer = scrollContainerRef.current as HTMLDivElement;

    scrollContainer.scroll({
      top: scrollContainer.scrollHeight,
    });
  }, [timeSlotAllocations]);

  return (
    <div>
      <div className={styles.dialogAllocateHeader}>{t("kmos.labels.allocatedLanes")}</div>
      {timeSlotAllocations === null ? (
        <CircularProgress
          style={{ margin: "0 auto", display: "block", marginBottom: 20 }}
        />
      ) : !timeSlotAllocations.length ? (
        <div style={{ marginBottom: 20 }}>{t("input.validations.noLanesAllocated")}</div>
      ) : (
        <div className={styles.dialogAllocationLaneContainer} ref={scrollContainerRef}>
          {saving && (
            <div className={styles.dialogAllocationLaneSaveOverlay}>
              <CircularProgress />
            </div>
          )}
          <div style={{ marginBottom: 20 }}>
            <Table>
              <TableHead className={styles.allocationHeader}>
                <AllocationRow>
                  <AllocationHeaderCellNumber></AllocationHeaderCellNumber>
                  <AllocationHeaderCellNumber>{t("kmos.labels.priority")}</AllocationHeaderCellNumber>
                  <AllocationHeaderCellText>{t("input.labels.zone")}</AllocationHeaderCellText>
                  <AllocationHeaderCellText>{t("kmos.labels.lane")}</AllocationHeaderCellText>
                  <AllocationHeaderCellNumber>{t("kmos.labels.capacity")}</AllocationHeaderCellNumber>
                  <AllocationHeaderCellNumber>{kmosManualAllocationEnabled == true && kmosManualAllocationReleased == true ? t("kmos.labels.allocated") : t("kmos.labels.arrived")}</AllocationHeaderCellNumber>
                  <AllocationHeaderCellNumber>{t("kmos.labels.parked")}</AllocationHeaderCellNumber>
                  <AllocationHeaderCellNumber>{t("kmos.labels.spaces")}</AllocationHeaderCellNumber>
                  <AllocationHeaderCellText>{t("booking.cards.status")}</AllocationHeaderCellText>
                  <AllocationHeaderCellNumber></AllocationHeaderCellNumber>
                </AllocationRow>
              </TableHead>
              <TableBody className={styles.dialogAllocationLaneTableBody}>
                {closedLanes?.map((allocationData) => {
                  const key = `${allocationData.allocation.id}-allocationTableRow`;

                  return (
                    <AllocationRowClosed key={key}>
                      {getAllocationRowCells(allocationData)}
                    </AllocationRowClosed>
                  );
                })}
                {openLanes?.map((allocationData, priority) => {
                  const key = `${allocationData.allocation.id}-allocationTableRow`;

                  return (
                    <AllocationRow key={key}>
                      {getAllocationRowCells(allocationData, priority + 1)}
                    </AllocationRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default KmosDialogLanes;
